import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import IconHome from "../../../styles/icons/IconHome";
import menuItems from "../../../utils/helpers/helperDataset/menuItems.json";
import "react-modern-drawer/dist/index.css";
import MobileDrawerDropdwonController from "./MobileDrawerDropdwonController";
import ProfileMenuController from "./ProfileMenuController";
import { useOrganisationStatus } from "../../../context/organisationStatus";

type UserRole = "GlobalAdmin" | "Administrator" | "Operator" | "Customer";

interface Permission {
  GlobalAdmin?: boolean;
  Administrator?: boolean;
  Operator?: boolean;
  Customer?: boolean;
}

interface MenuChild {
  name: string;
  url?: string;
  icon?: string;
  navigation: string;
  show?: boolean;
  permission?: Permission;
}

interface MenuItem {
  name: string;
  url?: string;
  icon?: string;
  navigation: string;
  show?: boolean;
  isClickable?: boolean;
  dropdownShow?: boolean;
  permission?: Permission;
  dropdown?: MenuChild[];
}

const MobileDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("");
  const [selectedDropdown, setSelectedDropdown] = useState("");
  const [selectedDropdownMenu, setSelectedDropdownMenu] = useState("");

  const { instance } = useMsal();
  const activeAccount = instance?.getActiveAccount();
  const { isOrganisationActive } = useOrganisationStatus();

  const userRole = String(
    activeAccount?.idTokenClaims?.extension_ilpr ?? ""
  ) as UserRole;

  const toggleDrawer = () => {
    setIsOpen((prevState) => !prevState);
  };

  const hasPermission = (permission?: Permission) => {
    if (!permission || !userRole) return false;
    return Boolean(permission[userRole]);
  };

  const filteredMenuList = useMemo(() => {
    // Users of a deactivated organisation can only reach their profile.
    if (!isOrganisationActive) return [];
    return (menuItems as MenuItem[])
      .filter((menu) => menu.show !== false)
      .filter((menu) => hasPermission(menu.permission))
      .map((menu) => {
        const filteredDropdown = (menu.dropdown || [])
          .filter((child) => child.show !== false)
          .filter((child) => hasPermission(child.permission));

        return {
          ...menu,
          dropdown: filteredDropdown,
        };
      })
      .filter((menu) => {
        const hasDropdown = (menu.dropdown || []).length > 0;
        const isClickable = Boolean(menu.isClickable);

        return isClickable || hasDropdown;
      });
  }, [userRole, isOrganisationActive]);

  function handleSelectMenu(name: string) {
    setSelectedMenu(name);
    setSelectedDropdown("");
    setSelectedDropdownMenu("");
    toggleDrawer();
  }

  function handleDropDown(name: string) {
    if (selectedDropdown === name) {
      setSelectedDropdown("");
    } else {
      setSelectedDropdown(name);
      setSelectedMenu(name);
    }
  }

  function handleParentMenuClick(menu: MenuItem) {
    const hasDropdown = (menu.dropdown || []).length > 0;

    if (hasDropdown) {
      handleDropDown(menu.name);
      return;
    }

    handleSelectMenu(menu.name);
  }

  function handleSetSelectedDropdownMenu(name: string) {
    setSelectedDropdownMenu(name);
    setSelectedDropdown("");
    toggleDrawer();
  }

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const BottomLine = (
    <div className="bottom-line">
      <div className="profile-section">
        <ProfileMenuController />
      </div>
      <div className="icon-section" onClick={handleLogout}>
        <IconHome breadcrumb="logout" activeColor="#007BFF" />
      </div>
    </div>
  );

  const renderMenuItem = (menu: MenuItem, index: number) => {
    const hasDropdown = (menu.dropdown || []).length > 0;
    const isDropdownOpen = selectedDropdown === menu.name;

    return (
      <div
        className="drawer-item"
        key={`${menu.name}-${menu.navigation || menu.url || "no-nav"}-${index}`}
      >
        {hasDropdown ? (
          <div
            className="list-styles-drawer"
            style={{ marginBottom: "20px", cursor: "pointer" }}
            onClick={() => handleParentMenuClick(menu)}
          >
            <div className="menu-item-drawer">
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                {menu.icon && (
                  <div className="logo">
                    <IconHome breadcrumb={menu.icon} activeColor="#FAFAFA" />
                  </div>
                )}
                <div className="menu-item-name">{menu.name}</div>
              </div>

              <div className="dropdown-arrow">
                <IconHome
                  breadcrumb="dropdown"
                  activeColor={isDropdownOpen ? "#FAFAFA" : "#007BFF"}
                />
              </div>
            </div>
          </div>
        ) : (
          <Link
            to={menu.navigation}
            className="list-styles-drawer"
            style={{ marginBottom: "20px" }}
            onClick={() => handleSelectMenu(menu.name)}
          >
            <div className="menu-item-drawer">
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                {menu.icon && (
                  <div className="logo">
                    <IconHome breadcrumb={menu.icon} activeColor="#FAFAFA" />
                  </div>
                )}
                <div className="menu-item-name">{menu.name}</div>
              </div>
            </div>
          </Link>
        )}

        {hasDropdown && (
          <MobileDrawerDropdwonController
            menuItemName={menu.name}
            dropdownMenuList={menu.dropdown || []}
            isOpen={isDropdownOpen}
            handleSetSelectedDropdownMenu={handleSetSelectedDropdownMenu}
            selectedDropdownMenu={selectedDropdownMenu}
          />
        )}
      </div>
    );
  };

  return (
    <div className="drawer">
      <div className="drawer-btn" onClick={toggleDrawer}>
        <IconHome breadcrumb="drawer" />
      </div>

      {isOpen ? (
        <div className="drawer-div">
          <div className="drawer-menus">
            {filteredMenuList.map(renderMenuItem)}

            {(userRole === "Administrator" || userRole === "GlobalAdmin") && (
              <>
                {userRole === "GlobalAdmin" && (
                  <div className="drawer-item">
                    <Link
                      to="/organisations"
                      className="list-styles-drawer"
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="menu-item-drawer">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <IconHome
                            breadcrumb="companyIcon"
                            activeColor="#ffffff"
                          />
                          <div className="menu-item-name">Organisations</div>
                        </div>
                      </div>
                    </Link>
                  </div>
                )}
                <div className="drawer-item">
                  <Link
                    to="/customers"
                    className="list-styles-drawer"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="menu-item-drawer">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <IconHome
                          breadcrumb="customers"
                          activeColor="#ffffff"
                        />
                        <div className="menu-item-name">Customers</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="drawer-item">
                  <Link
                    to="/jobs"
                    className="list-styles-drawer"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="menu-item-drawer">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <IconHome breadcrumb="jobs" activeColor="#ffffff" />
                        <div className="menu-item-name">Jobs</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="drawer-item">
                  <Link
                    to="/user/users"
                    className="list-styles-drawer"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="menu-item-drawer">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <IconHome
                          breadcrumb="userManagement"
                          activeColor="#ffffff"
                        />
                        <div className="menu-item-name">Manage Users</div>
                      </div>
                    </div>
                  </Link>
                </div>
                {/* <div className="drawer-item">
                  <Link
                    to="/user/requests"
                    className="list-styles-drawer"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="menu-item-drawer">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <IconHome
                          breadcrumb="userManagement"
                          activeColor="#ffffff"
                        />
                        <div className="menu-item-name">Manage Requests</div>
                      </div>
                    </div>
                  </Link>
                </div> */}
              </>
            )}
          </div>
          {BottomLine}
        </div>
      ) : null}
    </div>
  );
};

export default MobileDrawer;
